import * as React from "react";
import { Container, Tabs, Tab, Alert, Card, Col, Row } from "react-bootstrap";
import * as JSONbig from "json-bigint";
import I18n from "lib/I18n";
import { Loader } from "lib/components/Loader";
import style from "./style.module.css";
import { DataEntryList } from "../DataEntryList";
import { WrapCode } from "lib/components/WrapCode";
import { getStore } from "../../store";
import { Item } from "../../store/Item";
import { observer } from "mobx-react-lite";

const systemKeys = [
  "application_id",
  "event_id",
  "env",
  "operation_mode",
  "product_model",
  "os_version",
  "rebootless_system_update_version",
  "serial_number",
  "is_china",
  "is_japan",
  "lc_recorded_at",
  "nc_recorded_at",
  "posted_at",
  "application_version",
  "device_id",
  "device_id2",
  "nsa_id",
  "report_identifier",
] as const;

export const Detail = observer(() => {
  const { detail } = getStore();

  const onSelect = (key: string | null) => {
    if (!key) {
      return;
    }
    detail.tab = key;
  };

  const prettyJson = (item: Item) => {
    try {
      return JSONbig.stringify(JSONbig.parse(item.json), undefined, "  ");
    } catch (_) {
      return null;
    }
  };

  const renderPrettyJson = (item: Item) => {
    return (
      <Card bg="light">
        <Card.Body>
          <pre className="detail-pretty-json">
            <code>{prettyJson(item)}</code>
          </pre>
        </Card.Body>
      </Card>
    );
  };

  const renderRawJson = (item: Item) => {
    return (
      <Card bg="light">
        <Card.Body>
          <pre className="detail-raw-json">
            <WrapCode>{item.json}</WrapCode>
          </pre>
        </Card.Body>
      </Card>
    );
  };

  const renderList = (item: Item) => {
    return (
      <div className="detail-list-json">
        <DataEntryList item={item} />
      </div>
    );
  };

  const renderTimezoneNotice = () => {
    return (
      <Alert variant="warning">
        {I18n.t("raw_post_viewer.messages.timezone_notice")}
      </Alert>
    );
  };

  const renderSystemKeyDescription = () => {
    return (
      <div>
        <h3>{I18n.t("raw_post_viewer.label.system_entries")}</h3>
        <p>{I18n.t("raw_post_viewer.messages.system_key_description")}</p>
      </div>
    );
  };

  const renderSystemKeyDetails = () => {
    return (
      <Card bg="light">
        <Card.Body>
          <Container>
            {systemKeys.map((systemKey) => (
              <Row key={systemKey}>
                <Col xs={3}>{systemKey}</Col>
                <Col xs={6}>{I18n.t(`system_keys.${systemKey}`)}</Col>
              </Row>
            ))}
          </Container>
        </Card.Body>
      </Card>
    );
  };

  const renderContent = (item: Item) => {
    const tabProps = {
      className: style.tabContent,
      tabClassName: style.tab,
    };

    return (
      <div className={`${style.root} detail`}>
        <Tabs
          id="detail"
          transition={false}
          activeKey={detail.tab}
          defaultActiveKey={detail.tab}
          onSelect={onSelect}
        >
          <Tab
            eventKey="list"
            title={I18n.t("raw_post_viewer.label.list")}
            {...tabProps}
          >
            {renderList(item)}
          </Tab>
          <Tab
            eventKey="pretty"
            title={I18n.t("raw_post_viewer.label.pretty")}
            {...tabProps}
          >
            {renderPrettyJson(item)}
          </Tab>
          <Tab
            eventKey="raw"
            title={I18n.t("raw_post_viewer.label.raw")}
            {...tabProps}
          >
            {renderRawJson(item)}
          </Tab>
          <Tab
            eventKey="help"
            title={I18n.t("raw_post_viewer.label.help")}
            {...tabProps}
          >
            {renderSystemKeyDescription()}
            {renderSystemKeyDetails()}
            {renderTimezoneNotice()}
          </Tab>
        </Tabs>
      </div>
    );
  };

  if (detail.item != null) {
    return (
      <Loader show={detail.isLoading}>{renderContent(detail.item)}</Loader>
    );
  }
  return (
    <Loader show={detail.isLoading}>
      <div />
    </Loader>
  );
});
